import { useEffect, useMemo, useState } from "react"
import { Radio, Space } from "antd"
import { FullscreenContentModal } from "components/ui/modals/FullscreenContentModal/FullscreenContentModal"
import { Select } from "components/ui/select/Select"
import { Input } from "components/ui/inputs/Input"
import { useSearchParams } from "react-router-dom"
import { useProcessings } from "hooks/api/drawings/drawings.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonGetBack } from "components/ui/buttons/ButtonGetBack/ButtonGetBack"
import { useCreateQuotation, useGetQuotations } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { isMissingProjectNumberError } from "helpers/projectNumber.helper"
import { useMessage } from "hooks/message.hook"
import { useQueryClient } from "@tanstack/react-query"
import { QuotationConfiguration, type TQuotationConfig } from "components/ui/QuotationConfiguration/QuotationConfiguration"
import { createEmptyDeliveryDate, hasIncompleteDeliveryDate } from "components/ui/QuotationConfiguration/quotationConfiguration.helper"
import { formatDate } from "helpers/utils.helper"
import { DATE_TIME_FORMAT } from "helpers/dateFormat.helper"
import { parseProcessingRunKey } from "helpers/processingRun.helper"
import type { DefaultOptionType } from "antd/es/select"
import type { TOnChangeInput } from "types/default.type"
import style from "./CreateQuotationModal.module.scss"

const { content, title, field, fieldLabel, radioGroup, select, barStep2, barButton, quotationOptionDate, required } =
	style

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	// REQ-377: invoked when the backend rejects creation because the drawing has no project number,
	// so the caller can surface the project-number gate instead of a raw error toast.
	onMissingProjectNumber?: () => void
}

export const CreateQuotationModal = ({ isModalOpen, setModalClose, onMissingProjectNumber }: Props) => {
	const [step, setStep] = useState<1 | 2>(1)
	const [name, setName] = useState("")
	const [processingId, setProcessingId] = useState<number | undefined>()
	const [quotationId, setQuotationId] = useState<number | undefined>()
	const [sourceType, setSourceType] = useState<"processing" | "quotation">("processing")
	const [config, setConfig] = useState<TQuotationConfig>(() => ({ deliveryDates: [createEmptyDeliveryDate()] }))

	const [searchParams, setSearchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const { data: processingRuns, isPending } = useProcessings(drawingId)
	const { data: quotations, isPending: isQuotationsPending } = useGetQuotations(drawingId)
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { success: successMessage } = useMessage()
	const { mutate: createQuotationMutation, isPending: isCreateQuotationPending } = useCreateQuotation()

	const selectOptions: DefaultOptionType[] = useMemo(
		() =>
			Object.entries(processingRuns || {}).map(([label, value]) => {
				const { date: formattedDate, isLatest } = parseProcessingRunKey(label)
				return {
					label: isLatest ? (
						<>
							{formattedDate}
							<span className={quotationOptionDate}> (latest)</span>
						</>
					) : (
						formattedDate
					),
					value: value as number,
				}
			}),
		[processingRuns],
	)

	const quotationOptions: DefaultOptionType[] = useMemo(
		() =>
			(quotations || []).map((quotation) => {
				const formattedDate = formatDate(quotation.creation_datetime, "yyyy-MM-dd'T'HH:mm:ss'Z'", DATE_TIME_FORMAT)
				return {
					label: quotation.name ? (
						<>
							{quotation.name} <span className={quotationOptionDate}>({formattedDate})</span>
						</>
					) : (
						formattedDate
					),
					value: quotation.id,
				}
			}),
		[quotations],
	)

	useEffect(() => {
		if (selectOptions.length > 0 && !processingId) {
			setProcessingId(selectOptions[0].value as number)
		}
	}, [selectOptions, processingId])

	useEffect(() => {
		if (quotationOptions.length > 0 && !quotationId) {
			setQuotationId(quotationOptions[0].value as number)
		}
	}, [quotationOptions, quotationId])

	useEffect(() => {
		if (sourceType === "quotation" && quotationOptions.length === 0) {
			setSourceType("processing")
		}
	}, [sourceType, quotationOptions])

	const handleClose = () => {
		setStep(1)
		setName("")
		setConfig({ deliveryDates: [createEmptyDeliveryDate()] })
		setModalClose()
	}

	if (isPending || isQuotationsPending) {
		return (
			<FullscreenContentModal isOpen={isModalOpen} onClose={handleClose}>
				<div className={content}>
					<h2 className={title}>Create quotation</h2>
					<div data-testid="skeleton">
						<Skeleton rows={4} />
					</div>
				</div>
			</FullscreenContentModal>
		)
	}

	const onChangeProcessingSelectHandle = (value: string | number) => {
		setProcessingId(value as number)
		const newParams = new URLSearchParams(searchParams)
		newParams.set("processingID", String(value))
		setSearchParams(newParams, { replace: true })
	}

	const onChangeQuotationSelectHandle = (value: string | number) => {
		setQuotationId(value as number)
	}

	const onNameChangeHandle = (e: TOnChangeInput) => {
		setName(e.target.value)
	}

	const onContinueStep1 = () => {
		setStep(2)
	}

	const onBackStep2 = () => {
		setStep(1)
	}

	const canSubmit =
		((sourceType === "processing" && !!processingId && !!config.seriesId) ||
			(sourceType === "quotation" && !!quotationId)) &&
		!hasIncompleteDeliveryDate(config.deliveryDates)

	const createQuotationHandle = () => {
		if (!canSubmit) return

		const url = new URL(window.location.href)
		url.searchParams.set("drawingID", String(drawingId))
		url.searchParams.set("tab", sourceType === "processing" ? "documents" : "quotations")

		const sourceEntityId = sourceType === "processing" ? processingId! : quotationId!

		if (sourceType === "processing") {
			url.searchParams.set("processingID", String(processingId))
		}

		if (sourceType === "quotation") {
			url.searchParams.set("quotationID", String(quotationId))
		}

		const trimmedName = name.trim()
		const deliveryDates = config.deliveryDates
			.filter((d): d is { key: string; id: number | null; date: string; name?: string } => !!d.date)
			.map((d) => ({ date: d.date, ...(d.name?.trim() ? { project_name: d.name.trim() } : {}) }))

		createQuotationMutation(
			{
				drawing_id: drawingId,
				source_entity_id: sourceEntityId,
				source_entity_type: sourceType,
				source_url: url.toString(),
				processing_url: url.toString(),
				...(trimmedName ? { name: trimmedName } : {}),
				...(sourceType === "processing"
					? {
							...(config.seriesId ? { series_id: config.seriesId } : {}),
							...(config.pullCollectionId ? { pull_collection_id: config.pullCollectionId } : {}),
							...(config.drawerSlideTypeId ? { drawer_slide_type_id: config.drawerSlideTypeId } : {}),
							...(config.drawerBoxesMaterialId ? { drawer_boxes_material_id: config.drawerBoxesMaterialId } : {}),
							...(config.hingesTypeId ? { hinges_type_id: config.hingesTypeId } : {}),
							...(deliveryDates.length > 0 ? { delivery_dates: deliveryDates } : {}),
						}
					: {}),
			},
			{
				onSuccess: () => {
					successMessage("Quotation created successfully")
					queryClient.invalidateQueries({ queryKey: ["quotations", drawingId] })
					handleClose()
				},
				onError: (error) => {
					if (isMissingProjectNumberError(error)) {
						onMissingProjectNumber?.()
						return
					}
					formatError(error)
				},
			},
		)
	}

	const isNameProvided = !!name.trim()

	const bottomBar =
		step === 1 ? (
			sourceType === "quotation" ? (
				<ButtonPrimary
					className={barButton}
					onClick={createQuotationHandle}
					submittable={isNameProvided && !!quotationId && !isCreateQuotationPending}
				>
					Create quotation
				</ButtonPrimary>
			) : (
				<ButtonPrimary className={barButton} onClick={onContinueStep1} submittable={isNameProvided}>
					Continue
				</ButtonPrimary>
			)
		) : (
			<div className={barStep2}>
				<ButtonGetBack onClick={onBackStep2}>Back</ButtonGetBack>
				<ButtonPrimary
					className={barButton}
					onClick={createQuotationHandle}
					submittable={canSubmit && !isCreateQuotationPending}
				>
					Create quotation
				</ButtonPrimary>
			</div>
		)

	return (
		<FullscreenContentModal isOpen={isModalOpen} onClose={handleClose} bottomBar={bottomBar}>
			<div className={content}>
				<h2 className={title}>{step === 1 ? "Create quotation" : "Quotation configuration"}</h2>
				{step === 1 ? (
					<>
						<div className={field}>
							<span className={fieldLabel}>
								<span>Name</span>
								<span className={required}>*</span>
							</span>
							<Input value={name} onChange={onNameChangeHandle} />
						</div>
						{quotationOptions.length > 0 ? (
							<div className={field}>
								<span className={fieldLabel}>Select source</span>
								<Radio.Group className={radioGroup} value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
									<Space direction="vertical">
										<Radio value="processing">Processing</Radio>
										<Radio value="quotation">Quotation</Radio>
									</Space>
								</Radio.Group>
							</div>
						) : null}
						<div className={field}>
							<span className={fieldLabel}>{sourceType === "processing" ? "Processing run" : "Quotations"}</span>
							{sourceType === "processing" ? (
								<Select
									options={selectOptions}
									value={processingId}
									onChange={onChangeProcessingSelectHandle}
									className={select}
									variant="outlined"
									showSearch
									optionFilterProp="label"
								/>
							) : (
								<Select
									options={quotationOptions}
									value={quotationId}
									onChange={onChangeQuotationSelectHandle}
									className={select}
									variant="outlined"
									showSearch
									optionFilterProp="label"
								/>
							)}
						</div>
					</>
				) : (
					<QuotationConfiguration
						config={config}
						setConfig={setConfig}
						quotationId={sourceType === "quotation" ? quotationId : undefined}
					/>
				)}
			</div>
		</FullscreenContentModal>
	)
}
